'use client';

import React, { useState } from 'react';
import { AlertTriangle, TrendingUp, TrendingDown, Clock, CheckCircle, Briefcase, Truck, ShoppingCart, RefreshCw, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import StatusBadge from '@/components/ui/StatusBadge';
import ARAPChart from './ARAPChart';
import InvoiceAgingChart from './InvoiceAgingChart';
import Icon from '@/components/ui/AppIcon';


type Role = 'finance' | 'ops';

const financeKPIs = [
  {
    id: 'kpi-ar-outstanding',
    label: 'AR outstanding',
    value: '£184,320',
    sub: '23 open invoices',
    trend: '+£12,400 vs last week',
    trendUp: true,
    icon: TrendingUp,
    color: 'var(--foreground)',
    bg: 'var(--card)',
    accent: 'var(--accent)',
    hero: true,
  },
  {
    id: 'kpi-ap-owed',
    label: 'AP owed to suppliers',
    value: '£97,840',
    sub: '15 open PO invoices',
    trend: '-£5,200 vs last week',
    trendUp: false,
    icon: TrendingDown,
    color: 'var(--foreground)',
    bg: 'var(--card)',
    accent: 'var(--slate)',
    hero: false,
  },
  {
    id: 'kpi-overdue-ar',
    label: 'Overdue AR invoices',
    value: '£31,450',
    sub: '7 invoices — action required',
    trend: '+2 since yesterday',
    trendUp: true,
    icon: AlertTriangle,
    color: '#B91C1C',
    bg: '#FEF2F2',
    accent: '#EF4444',
    hero: false,
    overdue: true,
  },
  {
    id: 'kpi-overdue-ap',
    label: 'Overdue AP invoices',
    value: '£14,220',
    sub: '4 invoices — supplier risk',
    trend: 'Same as yesterday',
    trendUp: false,
    icon: AlertTriangle,
    color: '#B87A1A',
    bg: 'var(--amber-light)',
    accent: 'var(--amber)',
    hero: false,
    overdue: true,
  },
  {
    id: 'kpi-due-soon',
    label: 'Due in next 7 days',
    value: '£28,600',
    sub: '9 invoices',
    trend: '3 due tomorrow',
    trendUp: false,
    icon: Clock,
    color: '#B87A1A',
    bg: 'var(--amber-light)',
    accent: 'var(--amber)',
    hero: false,
  },
  {
    id: 'kpi-paid-month',
    label: 'Paid this month',
    value: '£142,780',
    sub: '31 invoices settled',
    trend: '+18% vs Aug 2025',
    trendUp: true,
    icon: CheckCircle,
    color: 'var(--accent)',
    bg: 'var(--teal-light)',
    accent: 'var(--accent)',
    hero: false,
  },
  {
    id: 'kpi-unreconciled',
    label: 'Unreconciled payments',
    value: '8',
    sub: '£22,310 to match',
    trend: 'Oldest: 12 days ago',
    trendUp: false,
    icon: RefreshCw,
    color: '#B87A1A',
    bg: 'var(--amber-light)',
    accent: 'var(--amber)',
    hero: false,
  },
  {
    id: 'kpi-reconciled-month',
    label: 'Reconciled this month',
    value: '£118,440',
    sub: '28 records matched',
    trend: 'All matched to invoices',
    trendUp: true,
    icon: CheckCircle,
    color: 'var(--accent)',
    bg: 'var(--teal-light)',
    accent: 'var(--accent)',
    hero: false,
  },
];

const opsKPIs = [
  {
    id: 'ops-active-jobs',
    label: 'Active jobs',
    value: '14',
    sub: '3 new this week',
    trend: 'From CRM',
    trendUp: true,
    icon: Briefcase,
    color: 'var(--foreground)',
    bg: 'var(--card)',
    accent: 'var(--accent)',
    hero: true,
  },
  {
    id: 'ops-deliveries-today',
    label: 'Deliveries due today',
    value: '4',
    sub: '2 in progress',
    trend: '1 overdue',
    trendUp: false,
    icon: Truck,
    color: '#B91C1C',
    bg: '#FEF2F2',
    accent: '#EF4444',
    hero: false,
    overdue: true,
  },
  {
    id: 'ops-deliveries-week',
    label: 'Due this week',
    value: '11',
    sub: '7 pending, 4 in progress',
    trend: '3 on track',
    trendUp: true,
    icon: Truck,
    color: 'var(--foreground)',
    bg: 'var(--card)',
    accent: 'var(--slate)',
    hero: false,
  },
  {
    id: 'ops-po-pending',
    label: 'POs awaiting acknowledgement',
    value: '5',
    sub: 'Across 3 suppliers',
    trend: 'Oldest: 4 days',
    trendUp: false,
    icon: ShoppingCart,
    color: '#B87A1A',
    bg: 'var(--amber-light)',
    accent: 'var(--amber)',
    hero: false,
    overdue: true,
  },
  {
    id: 'ops-pos-received',
    label: 'POs received this month',
    value: '38',
    sub: '£204,600 total value',
    trend: '+6 vs last month',
    trendUp: true,
    icon: ShoppingCart,
    color: 'var(--accent)',
    bg: 'var(--teal-light)',
    accent: 'var(--accent)',
    hero: false,
  },
  {
    id: 'ops-complete-jobs',
    label: 'Jobs completed this month',
    value: '9',
    sub: 'All deliveries confirmed',
    trend: '100% on-time rate',
    trendUp: true,
    icon: CheckCircle,
    color: 'var(--accent)',
    bg: 'var(--teal-light)',
    accent: 'var(--accent)',
    hero: false,
  },
];

const recentInvoices = [
  { id: 'inv-10482', number: 'INV-10482', job: 'Merch drop — TechConf 2026', client: 'TechConf Events Ltd', amount: '£8,400', status: 'overdue', daysLabel: '6 days overdue', type: 'AR' },
  { id: 'inv-10481', number: 'INV-10481', job: 'Branded hoodies — RetailCo Q3', client: 'RetailCo UK', amount: '£14,220', status: 'due', daysLabel: 'Due tomorrow', type: 'AR' },
  { id: 'inv-10480', number: 'INV-10480', job: 'Tote bags — GreenFest', client: 'GreenFest CIC', amount: '£3,150', status: 'paid', daysLabel: 'Paid 18 Aug', type: 'AR' },
  { id: 'inv-10479', number: 'INV-10479', job: 'Promo caps — SportsBrand', client: 'SportsBrand Ltd', amount: '£6,800', status: 'reconciled', daysLabel: 'Reconciled', type: 'AR' },
  { id: 'inv-10478', number: 'INV-10478', job: 'Embroidered polos — HealthOrg', client: 'HealthOrg NHS', amount: '£11,640', status: 'sent', daysLabel: 'Due 28 Aug', type: 'AR' },
];

const activeJobs = [
  { id: 'job-crm-9924', ref: 'JOB-CRM-9924', name: 'Merch drop — TechConf 2026', client: 'TechConf Events Ltd', deliveries: 4, status: 'active' },
  { id: 'job-crm-9921', ref: 'JOB-CRM-9921', name: 'Branded hoodies — RetailCo Q3', client: 'RetailCo UK', deliveries: 2, status: 'active' },
  { id: 'job-crm-9919', ref: 'JOB-CRM-9919', name: 'Promo kit — SaaS Summit', client: 'SaaS Summit Ltd', deliveries: 6, status: 'active' },
  { id: 'job-crm-9917', ref: 'JOB-CRM-9917', name: 'Tote bags — GreenFest', client: 'GreenFest CIC', deliveries: 3, status: 'in-progress' },
  { id: 'job-crm-9915', ref: 'JOB-CRM-9915', name: 'Embroidered polos — HealthOrg', client: 'HealthOrg NHS', deliveries: 5, status: 'active' },
];

export default function DashboardContent() {
  const [role, setRole] = useState<Role>('finance');
  const kpis = role === 'finance' ? financeKPIs : opsKPIs;

  // Grid plan: 8 cards → grid-cols-4 → row 1: hero spans 2 cols + 2 regular; row 2: 4 regular
  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: 'var(--foreground)' }}>
            Good afternoon, Marcus
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--slate)' }}>
            Friday, 21 August 2026 — here is what needs your attention today
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Role toggle */}
          <div
            className="flex items-center rounded-lg p-1 border"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
          >
            {(['finance', 'ops'] as Role[]).map((r) => (
              <button
                key={`role-${r}`}
                onClick={() => setRole(r)}
                className="px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-150"
                style={{
                  backgroundColor: role === r ? 'var(--ink)' : 'transparent',
                  color: role === r ? 'white' : 'var(--slate)',
                }}
              >
                {r === 'finance' ? 'Finance view' : 'Ops view'}
              </button>
            ))}
          </div>
          <span className="text-xs px-2 py-1 rounded-md" style={{ backgroundColor: 'var(--muted)', color: 'var(--slate)' }}>
            Updated 2 min ago
          </span>
        </div>
      </div>

      {/* KPI Bento Grid — 8 cards, grid-cols-4 */}
      {/* Row 1: hero (spans 2 cols) + 2 regular; Row 2: 4 regular */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          const isHero = kpi.hero;
          return (
            <div
              key={kpi.id}
              className={`rounded-xl border p-5 flex flex-col gap-3 transition-shadow hover:shadow-md ${isHero ? 'sm:col-span-2' : ''}`}
              style={{ backgroundColor: kpi.bg, borderColor: kpi.overdue ? (kpi.color === '#B91C1C' ? '#FECACA' : '#FDE8C4') : 'var(--border)' }}
            >
              <div className="flex items-center justify-between">
                <span
                  className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: kpi.overdue ? kpi.color : 'var(--slate)', letterSpacing: '0.06em' }}
                >
                  {kpi.label}
                </span>
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${kpi.accent}18` }}
                >
                  <Icon size={16} style={{ color: kpi.accent }} />
                </div>
              </div>
              <div>
                <p
                  className={`tabular-nums font-bold ${isHero ? 'text-3xl' : 'text-2xl'}`}
                  style={{ color: kpi.color, letterSpacing: '-0.02em' }}
                >
                  {kpi.value}
                </p>
                <p className="text-xs mt-1" style={{ color: kpi.overdue ? kpi.color : 'var(--slate)' }}>
                  {kpi.sub}
                </p>
              </div>
              <p
                className={`text-xs font-medium ${kpi.overdue ? 'pulse-amber' : ''}`}
                style={{ color: kpi.overdue ? kpi.accent : (kpi.trendUp ? 'var(--accent)' : 'var(--slate)') }}
              >
                {kpi.trend}
              </p>
            </div>
          );
        })}
      </div>

      {/* Charts row */}
      {role === 'finance' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div
            className="xl:col-span-2 rounded-xl border p-5"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>AR / AP outstanding — 8 weeks</h2>
                <p className="text-xs mt-0.5" style={{ color: 'var(--slate)' }}>Weekly snapshot of open invoice balances</p>
              </div>
            </div>
            <ARAPChart />
          </div>
          <div
            className="rounded-xl border p-5"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
          >
            <div className="mb-4">
              <h2 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Invoice aging</h2>
              <p className="text-xs mt-0.5" style={{ color: 'var(--slate)' }}>AR invoices by days outstanding</p>
            </div>
            <InvoiceAgingChart />
          </div>
        </div>
      )}

      {/* Bottom lists */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Recent invoices */}
        {role === 'finance' && (
          <div
            className="rounded-xl border"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
          >
            <div
              className="flex items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: 'var(--border)' }}
            >
              <h2 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Recent invoices</h2>
              <Link
                href="/invoice-management"
                className="flex items-center gap-1 text-xs font-medium"
                style={{ color: 'var(--accent)' }}
              >
                View all <ArrowRight size={12} />
              </Link>
            </div>
            <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
              {recentInvoices.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-center justify-between px-5 py-3 hover:bg-muted/40 transition-colors cursor-pointer"
                  style={inv.status === 'overdue' ? { backgroundColor: 'var(--amber-light)' } : {}}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{inv.number}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: 'var(--muted)', color: 'var(--slate)' }}>{inv.type}</span>
                    </div>
                    <p className="text-xs truncate mt-0.5" style={{ color: 'var(--slate)' }}>{inv.client} · {inv.job}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--foreground)' }}>{inv.amount}</span>
                    <StatusBadge status={inv.status as never} pulse={inv.status === 'overdue'} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active jobs */}
        <div
          className="rounded-xl border"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
        >
          <div
            className="flex items-center justify-between px-5 py-4 border-b"
            style={{ borderColor: 'var(--border)' }}
          >
            <h2 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Active jobs</h2>
            <Link
              href="/jobs"
              className="flex items-center gap-1 text-xs font-medium"
              style={{ color: 'var(--accent)' }}
            >
              View all <ArrowRight size={12} />
            </Link>
          </div>
          <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {activeJobs.map((job) => (
              <div
                key={job.id}
                className="flex items-center justify-between px-5 py-3 hover:bg-muted/40 transition-colors cursor-pointer"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono" style={{ color: 'var(--slate)' }}>{job.ref}</span>
                  </div>
                  <p className="text-sm font-medium truncate mt-0.5" style={{ color: 'var(--foreground)' }}>{job.name}</p>
                  <p className="text-xs" style={{ color: 'var(--slate)' }}>{job.client}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                  <span className="text-xs" style={{ color: 'var(--slate)' }}>{job.deliveries} deliveries</span>
                  <StatusBadge status={job.status as never} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}